import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Mic, Phone, PhoneIncoming, PhoneMissed, PhoneOutgoing } from 'lucide-react';
import AudioPlayback from './AudioPlayback';
import salesService from '../../services/sales';
import { formatDateTimeInTz } from '../../utils/timezone';
import { MATCH_KIND_LABEL, STATUS_LABEL, callLength, otherNumber } from '../../utils/calls';
import type { Call } from '../../types/sales';

/**
 * One call in a call log. A recorded call opens to its recording and
 * transcript for someone who may hear it (a Sales Admin, or the person on
 * the call); anyone else is told it was recorded and who can play it.
 */

function CallIcon({ call }: { call: Call }) {
  if (call.status === 'missed') return <PhoneMissed className="h-5 w-5 text-red-600 shrink-0" aria-hidden />;
  if (call.direction === 'outbound') return <PhoneOutgoing className="h-5 w-5 text-sky-600 shrink-0" aria-hidden />;
  if (call.direction === 'internal') return <Phone className="h-5 w-5 text-neutral-500 shrink-0" aria-hidden />;
  return <PhoneIncoming className="h-5 w-5 text-emerald-600 shrink-0" aria-hidden />;
}

export default function CallRow({ call, tz, linkCustomer = true }: { call: Call; tz: string; linkCustomer?: boolean }) {
  const [open, setOpen] = useState(false);
  const number = otherNumber(call);
  const who = call.matched_name || call.caller_name || number || 'Unknown number';
  const length = callLength(call.talk_sec ?? call.duration_sec);
  const directionText = call.direction === 'outbound' ? 'Called' : call.direction === 'internal' ? 'Internal' : 'Called in';
  const recorded = !!call.has_recording || !!call.has_transcript;

  return (
    <li className="p-3 min-w-0">
      <div className="flex items-start gap-3 min-w-0">
        <CallIcon call={call} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            {call.customer_id && linkCustomer ? (
              <Link to={`/accounts/customers/${call.customer_id}`} className="text-sm font-semibold text-primary-700 hover:underline break-words">
                {who}
              </Link>
            ) : (
              <span className="text-sm font-semibold text-neutral-900 break-words">{who}</span>
            )}
            {call.matched_kind && <span className="text-xs text-neutral-500">{MATCH_KIND_LABEL[call.matched_kind]}</span>}
          </div>
          <p className="text-sm text-neutral-600 break-words">
            {directionText}
            {number && call.matched_name ? ` · ${number}` : ''}
            {call.answered_by_name ? ` · ${call.answered_by_name}` : call.extension ? ` · ext ${call.extension}` : ''}
          </p>
          <p className="text-xs text-neutral-500 break-words">
            {formatDateTimeInTz(call.started_at, tz)}
            {' · '}{STATUS_LABEL[call.status]}
            {length && call.status !== 'missed' ? ` · ${length}` : ''}
            {call.recording_removed_at ? ' · recording deleted after the keeping period' : ''}
          </p>
        </div>
        {recorded && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="flex items-center gap-1 text-xs font-medium text-primary-700 px-2 py-1 rounded-lg hover:bg-primary-50 shrink-0"
          >
            <Mic className="h-4 w-4" aria-hidden />
            Recorded
            {open ? <ChevronUp className="h-4 w-4" aria-hidden /> : <ChevronDown className="h-4 w-4" aria-hidden />}
          </button>
        )}
      </div>
      {open && recorded && (
        <div className="mt-3 ml-8 space-y-2 min-w-0">
          {!call.can_listen ? (
            <p className="text-sm text-neutral-600 break-words">
              This call was recorded. A Sales Admin, or the person on the call, can play it and read its transcript.
            </p>
          ) : (
            <>
              {call.has_recording && (
                <AudioPlayback label="Play recording" load={() => salesService.callRecording(call.id)} />
              )}
              {call.transcript ? (
                <div className="rounded-lg bg-neutral-50 border border-neutral-200 p-3">
                  <p className="text-xs font-medium text-neutral-500 mb-1">Transcript (made by a machine; check the recording before relying on it)</p>
                  <p className="text-sm text-neutral-800 whitespace-pre-wrap break-words">{call.transcript}</p>
                </div>
              ) : (
                <p className="text-sm text-neutral-500">No transcript yet.</p>
              )}
            </>
          )}
        </div>
      )}
    </li>
  );
}
